<p-toast></p-toast>
<p-confirmDialog></p-confirmDialog>

<div class="client-details-page p-fluid">
    <app-page-shell>

        @if (client(); as activeClient) {
            <app-page-detail-hero [auditStamp]="{ createdAt: activeClient.createdAt, updatedAt: activeClient.updatedAt, createdBy: activeClient.createdByCustomerId, updatedBy: activeClient.updatedByCustomerId, createdByActor: activeClient.createdByActor, updatedByActor: activeClient.updatedByActor }"
                icon="pi pi-building"
                eyebrow="CRM Client"
                [title]="activeClient.companyName"
                [subtitle]="(activeClient.notifyingPersonName || 'No notifying person') + (activeClient.primaryEmail ? ' | ' + activeClient.primaryEmail : '')">
                @if (activeClient.logoUrl && !clientLogoLoadFailed()) {
                    <div titleMedia class="client-hero-logo">
                        <img [src]="activeClient.logoUrl" [alt]="activeClient.companyName + ' logo'" (error)="onClientLogoError()" />
                    </div>
                }
            <div tags>
                <p-tag [value]="activeClient.category || 'STANDARD'" [severity]="getCategorySeverity(activeClient.category || 'STANDARD')"></p-tag>
                <p-tag [value]="activeClient.businessType || 'Business type pending'" severity="secondary"></p-tag>
                <p-tag [value]="activeClient.currencyCode || 'SAR'" severity="info"></p-tag>
                <p-tag [value]="approvalStatusLabel(activeClient.approvalStatus)" [severity]="approvalSeverity(activeClient.approvalStatus)"></p-tag>
                @if (isCustomerClient(activeClient)) {
                    <p-tag [value]="'Credit ' + creditStatusLabel(getCreditStatus())" [severity]="creditSeverity(getCreditStatus())"></p-tag>
                }
                @if (activeClient.roles?.length) {
                    @for (role of activeClient.roles; track role) {
                        <p-tag [value]="roleLabel(role)" severity="contrast"></p-tag>
                    }
                } @else {
                    <p-tag value="Roles Pending" severity="warn"></p-tag>
                }
                @if (activeClient.linkedCompanyId) {
                    <p-tag value="Linked Company" severity="success"></p-tag>
                }
                </div>
                <div actions>
                    <p-button label="Back" icon="pi pi-arrow-left" severity="secondary" [outlined]="true" routerLink="/crm/clients"></p-button>
                    <p-button label="Create Quotation" icon="pi pi-file-plus" severity="secondary" [outlined]="true" (onClick)="createQuotation()"></p-button>
                    <p-button label="Create Agreement" icon="fa-solid fa-file-contract" severity="secondary" [outlined]="true" (onClick)="createAgreement()"></p-button>
                    @if (canApproveClient(activeClient)) {
                        <p-button label="Approve Client" icon="pi pi-check-circle" severity="success" [loading]="approvingClient()" (onClick)="approveClient()"></p-button>
                    }
                    <p-button label="Edit Client" icon="pi pi-pencil" severity="info" [routerLink]="['/crm/clients', activeClient.id, 'edit']"></p-button>
                </div>
            </app-page-detail-hero>

            <app-page-metric-strip [metrics]="metrics()"></app-page-metric-strip>

            <app-page-data-panel
                title="CRM Client Workspace"
                subtitle="Customer master, credit, compliance, contacts, documents, and follow-ups in one operating view."
                [resultText]="(activeClient.roles?.length || 0) + ' roles | ' + clientAccounts().length + ' client accounts'">
                <p-tabs [value]="activeTabValue" (valueChange)="onTabChange($event)" styleClass="client-workspace-tabs">
                    <p-tablist>
                        <p-tab [value]="0"><span class="workspace-tab"><i class="pi pi-gauge"></i><span>Overview</span></span></p-tab>
                        <p-tab [value]="1"><span class="workspace-tab"><i class="pi pi-history"></i><span>Tracking</span></span></p-tab>
                        <p-tab [value]="2"><span class="workspace-tab"><i class="pi pi-address-book"></i><span>Contacts</span><span class="workspace-badge">{{ contactDirectory().length }}</span></span></p-tab>
                        <p-tab [value]="3"><span class="workspace-tab"><i class="pi pi-briefcase"></i><span>Client Accounts</span><span class="workspace-badge">{{ clientAccounts().length }}</span></span></p-tab>
                        <p-tab [value]="4"><span class="workspace-tab"><i class="pi pi-folder"></i><span>Documents</span><span class="workspace-badge">{{ documents().length + registrationAttachments().length }}</span></span></p-tab>
                        <p-tab [value]="5"><span class="workspace-tab"><i class="pi pi-comment"></i><span>Remarks</span><span class="workspace-badge">{{ notes().length }}</span></span></p-tab>
                        <p-tab [value]="6"><span class="workspace-tab"><i class="pi pi-calendar"></i><span>Reminders</span><span class="workspace-badge">{{ reminders().length }}</span></span></p-tab>
                    </p-tablist>

                    <p-tabpanels>
                        <p-tabpanel [value]="0">
                            <section class="workspace-section">
                                <div class="client-route-board">
                                    <div class="client-route-endpoint">
                                        <span class="endpoint-kicker">CRM Client</span>
                                        <h3>{{ activeClient.companyName || '-' }}</h3>
                                        <p>{{ activeClient.customerNameLocal || activeClient.natureOfBusiness || 'Client identity pending' }}</p>
                                    </div>
                                    <div class="client-route-connector">
                                        <span></span>
                                        <i class="pi pi-tags"></i>
                                        <span></span>
                                    </div>
                                    <div class="client-route-endpoint destination">
                                        <span class="endpoint-kicker">Business Roles</span>
                                        <h3>{{ roleSummary(activeClient) }}</h3>
                                        <p>{{ activeClient.category || 'STANDARD' }} segment</p>
                                    </div>
                                </div>

                                <div class="client-summary-grid">
                                    <div><span>Segment</span><strong>{{ activeClient.category || 'STANDARD' }}</strong></div>
                                    <div><span>Business Type</span><strong>{{ activeClient.businessType || '-' }}</strong></div>
                                    <div><span>Currency</span><strong>{{ activeClient.currencyCode || 'SAR' }}</strong></div>
                                    <div><span>Credit</span><strong>{{ isCustomerClient(activeClient) ? creditStatusLabel(getCreditStatus()) : 'Not applicable' }}</strong></div>
                                    <div><span>Documents</span><strong>{{ documents().length + registrationAttachments().length }}</strong></div>
                                    <div><span>Activities</span><strong>{{ activities().length }}</strong></div>
                                    <div><span>Finance Approval</span><strong>{{ approvalStatusLabel(activeClient.approvalStatus) }}</strong></div>
                                </div>

                                <div class="client-overview-grid">
                                    <article class="info-block">
                                        <div class="block-heading"><i class="pi pi-building"></i><span>General</span></div>
                                        <div class="business-context-grid">
                                            <div><span>Customer</span><strong>{{ activeClient.companyName || '-' }}</strong></div>
                                            <div><span>Local Name</span><strong>{{ activeClient.customerNameLocal || '-' }}</strong></div>
                                            <div><span>Posting Date</span><strong>{{ activeClient.postingDate ? (activeClient.postingDate | date:'mediumDate') : '-' }}</strong></div>
                                            <div><span>Nature</span><strong>{{ activeClient.natureOfBusiness || '-' }}</strong></div>
                                            <div><span>Website</span><a *ngIf="activeClient.website" [href]="activeClient.website" target="_blank">{{ activeClient.website }}</a><strong *ngIf="!activeClient.website">-</strong></div>
                                            <div><span>Approved By</span><strong>{{ activeClient.approvedByCustomerName || '-' }}</strong></div>
                                            <div><span>Approved At</span><strong>{{ activeClient.approvedAt ? (activeClient.approvedAt | date:'medium') : '-' }}</strong></div>
                                        </div>
                                    </article>

                                    <article class="info-block">
                                        <div class="block-heading"><i class="pi pi-tags"></i><span>Business Context</span></div>
                                        <div class="business-context-grid">
                                            <div><span>Segment</span><strong>{{ activeClient.category || 'STANDARD' }}</strong></div>
                                            <div><span>Linked Agency</span><strong>{{ activeClient.linkedTransportationAgencyName || '-' }}</strong></div>
                                            <div><span>Linked Company</span><strong>{{ activeClient.linkedCompanyId ? linkedCompanyDisplayName() : '-' }}</strong></div>
                                            <div><span>Linked Customer</span><strong>{{ linkedCustomerDisplayName(activeClient) }}</strong></div>
                                        </div>
                                        <div class="role-chip-row">
                                            @if (activeClient.roles?.length) {
                                                @for (role of activeClient.roles; track role) {
                                                    <p-tag [value]="roleLabel(role)" severity="contrast"></p-tag>
                                                }
                                            } @else {
                                                <p-tag value="Roles pending classification" severity="warn"></p-tag>
                                            }
                                        </div>
                                    </article>

                                    <article class="info-block">
                                        <div class="block-heading"><i class="pi pi-shield"></i><span>Compliance</span></div>
                                        <div class="business-context-grid">
                                            <div><span>VAT / Tax</span><strong>{{ activeClient.taxRegNumber || '-' }}</strong></div>
                                            <div><span>CR Number</span><strong>{{ activeClient.crNumber || '-' }}</strong></div>
                                            <div><span>CR Expiry</span><strong>{{ activeClient.crExpiryDate ? (activeClient.crExpiryDate | date:'mediumDate') : '-' }}</strong></div>
                                            <div><span>Contract Expiry</span><strong>{{ activeClient.contractExpiryDate ? (activeClient.contractExpiryDate | date:'mediumDate') : '-' }}</strong></div>
                                        </div>
                                    </article>

                                    <article class="info-block">
                                        <div class="block-heading"><i class="fa-solid fa-file-contract"></i><span>Contract Health</span></div>
                                        <div class="business-context-grid">
                                            <div><span>Linked Agreements</span><strong>{{ contractContext()?.linkedContractCount || 0 }}</strong></div>
                                            <div><span>Next Expiry</span><strong>{{ contractContext()?.nextExpiryDate ? (contractContext()?.nextExpiryDate | date:'mediumDate') : '-' }}</strong></div>
                                            <div><span>Approved Quotes</span><strong>{{ contractContext()?.approvedQuotationAwaitingContractCount || 0 }} awaiting contract</strong></div>
                                        </div>
                                        <div class="role-chip-row">
                                            <p-tag *ngFor="let flag of getContractHealthFlags()" [value]="flag" severity="info"></p-tag>
                                            <p-tag *ngIf="getContractHealthFlags().length === 0" value="No contract flags" severity="secondary"></p-tag>
                                        </div>
                                    </article>

                                    @if (isCustomerClient(activeClient)) {
                                        <article class="info-block">
                                            <div class="block-heading"><i class="pi pi-credit-card"></i><span>Credit Control</span></div>
                                            <div class="business-context-grid">
                                                <div><span>Status</span><strong><p-tag [value]="creditStatusLabel(getCreditStatus())" [severity]="creditSeverity(getCreditStatus())"></p-tag></strong></div>
                                                <div><span>Credit Limit</span><strong>{{ creditControl()?.creditLimit !== null && creditControl()?.creditLimit !== undefined ? (creditControl()?.creditLimit | currency:(creditControl()?.currencyCode || activeClient.currencyCode || 'SAR'):'symbol') : '-' }}</strong></div>
                                                <div><span>Credit Period</span><strong>{{ creditControl()?.creditPeriodDays !== null && creditControl()?.creditPeriodDays !== undefined ? creditControl()?.creditPeriodDays + ' days' : '-' }}</strong></div>
                                                <div><span>Check Mode</span><strong>{{ creditCheckModeLabel(creditControl()?.creditCheckMode) }}</strong></div>
                                                <div><span>Overdue Grace</span><strong>{{ creditControl()?.overdueToleranceDays || 0 }} days</strong></div>
                                                <div><span>Manual Hold</span><strong>{{ creditControl()?.creditHold ? (creditControl()?.creditHoldReason || 'Yes') : 'No' }}</strong></div>
                                                <div><span>Open AR</span><strong>{{ (creditControl()?.openARAmount || 0) | currency:(creditControl()?.currencyCode || activeClient.currencyCode || 'SAR'):'symbol' }}</strong></div>
                                                <div><span>Overdue AR</span><strong>{{ (creditControl()?.overdueARAmount || 0) | currency:(creditControl()?.currencyCode || activeClient.currencyCode || 'SAR'):'symbol' }}</strong></div>
                                                <div><span>Available Credit</span><strong>{{ creditControl()?.availableCredit !== null && creditControl()?.availableCredit !== undefined ? (creditControl()?.availableCredit | currency:(creditControl()?.currencyCode || activeClient.currencyCode || 'SAR'):'symbol') : '-' }}</strong></div>
                                                <div><span>Receivables Profile</span><strong>{{ linkedCustomerDisplayName(activeClient) }}</strong></div>
                                            </div>
                                            @if (getCreditBlockers().length || getCreditWarnings().length) {
                                                <div class="credit-alerts">
                                                    @for (blocker of getCreditBlockers(); track blocker) {
                                                        <div class="credit-alert danger"><i class="pi pi-ban"></i><span>{{ blocker }}</span></div>
                                                    }
                                                    @for (warning of getCreditWarnings(); track warning) {
                                                        <div class="credit-alert warn"><i class="pi pi-exclamation-triangle"></i><span>{{ warning }}</span></div>
                                                    }
                                                </div>
                                            }
                                        </article>
                                    }

                                     <article class="info-block">
                                         <div class="block-heading"><i class="pi pi-address-book"></i><span>Primary Contact</span></div>
                                        <div class="person-line">
                                            <span class="contact-avatar"><i class="pi pi-user"></i></span>
                                            <div>
                                                <strong>{{ primaryMasterCommunication()?.name || activeClient.notifyingPersonName || 'Contact pending' }}</strong>
                                                <span>{{ primaryMasterCommunication()?.email || activeClient.primaryEmail || 'Email pending' }}</span>
                                            </div>
                                        </div>
                                        <dl>
                                            <div><dt>Phone</dt><dd>{{ primaryMasterCommunication()?.phone || activeClient.phoneNumber || '-' }}</dd></div>
                                            <div><dt>Mobile</dt><dd>{{ primaryMasterCommunication()?.mobile || activeClient.mobileNumber || '-' }}</dd></div>
                                            <div><dt>Contacts</dt><dd>{{ contactDirectory().length || 1 }}</dd></div>
                                        </dl>
                                        <div class="contact-summary-list compact-list">
                                            @for (contact of masterCommunications().slice(0, 3); track contact.id || $index) {
                                                <article class="contact-summary-row" [class.primary]="contact.primaryRecord">
                                                    <div>
                                                        <strong>{{ contact.name || communicationTypeLabel(contact.communicationType) }}</strong>
                                                        <span>{{ communicationTypeLabel(contact.communicationType) }}{{ contact.primaryRecord ? ' / Primary' : '' }}</span>
                                                    </div>
                                                    <div>
                                                        <a *ngIf="contact.email" [href]="'mailto:' + contact.email">{{ contact.email }}</a>
                                                        <span>{{ contact.phone || contact.mobile || '-' }}</span>
                                                    </div>
                                                </article>
                                            }
                                        </div>
                                     </article>

                                     <article class="info-block">
                                        <div class="block-heading"><i class="pi pi-map-marker"></i><span>Address</span></div>
                                        <div class="business-context-grid">
                                            <div><span>Street</span><strong>{{ address()?.street1 || '-' }}</strong></div>
                                            <div><span>Local Street</span><strong dir="rtl">{{ address()?.street1Local || '-' }}</strong></div>
                                            <div><span>City</span><strong>{{ address()?.city || '-' }}</strong></div>
                                            <div><span>Local City</span><strong dir="rtl">{{ address()?.cityLocal || '-' }}</strong></div>
                                            <div><span>Postal Code</span><strong>{{ address()?.postalCode || activeClient.postalCode || '-' }}</strong></div>
                                        </div>
                                    </article>
                                </div>
                            </section>
                        </p-tabpanel>

                        <p-tabpanel [value]="1">
                            <section class="workspace-section">
                                <div class="workspace-header">
                                    <div><h2>Unified Activity Timeline</h2><p>{{ activities().length }} activity records</p></div>
                                    <div class="workspace-actions">
                                        <p-button label="Log Activity" icon="pi pi-plus" (onClick)="showCommDialog.set(true)"></p-button>
                                        <p-button label="Add Note" icon="pi pi-pencil" severity="secondary" [outlined]="true" (onClick)="showNoteDialog.set(true)"></p-button>
                                    </div>
                                </div>
                                <p-timeline [value]="activities()" align="left" styleClass="customized-timeline">
                                    <ng-template pTemplate="marker" let-log><span class="timeline-marker" [class]="getActivitySeverity(log.activityType)"><i [class]="getActivityIcon(log.activityType)"></i></span></ng-template>
                                    <ng-template pTemplate="content" let-log>
                                        <div class="timeline-card">
                                            <div class="timeline-title">{{ log.title }}{{ log.status ? ' - ' + log.status : '' }}</div>
                                            <span>{{ log.occurredAt | date:'medium' }}</span>
                                            <p>{{ log.summary }}</p>
                                        </div>
                                    </ng-template>
                                </p-timeline>
                                @if (activities().length === 0) {
                                    <div class="empty-state"><i class="pi pi-history"></i><strong>No activities recorded</strong><span>Log calls, emails, meetings, notes, and reminders.</span></div>
                                }
                            </section>
                        </p-tabpanel>

                        <p-tabpanel [value]="2">
                            <section class="workspace-section">
                                <div class="workspace-header">
                                    <div><h2>Client Contact Directory</h2><p>{{ contactDirectory().length }} contacts from the client, linked company, and client accounts</p></div>
                                    <div class="workspace-actions"><p-button label="Add Contact" icon="pi pi-plus" (onClick)="startAddMasterCommunication()"></p-button></div>
                                </div>
                                <p-table [value]="contactDirectory()" styleClass="p-datatable-sm reminders-table">
                                    <ng-template pTemplate="header"><tr><th>Sources</th><th>Name</th><th>Email</th><th>Phone</th><th>Mobile</th><th></th></tr></ng-template>
                                    <ng-template pTemplate="body" let-row>
                                        <tr>
                                            <td>
                                                <div class="directory-badge-list">
                                                    <p-tag *ngIf="row.clientContact" value="Client contact" [severity]="row.primaryRecord ? 'success' : 'info'"></p-tag>
                                                    <p-tag *ngIf="row.linkedCompanyContact" value="Linked company" severity="success"></p-tag>
                                                    <p-tag *ngFor="let account of row.accounts || []" [value]="'Account: ' + (account.name || account.id)" severity="secondary"></p-tag>
                                                    <p-tag *ngIf="row.primaryRecord" value="Primary" severity="warn"></p-tag>
                                                </div>
                                            </td>
                                            <td>{{ row.name || '-' }}</td>
                                            <td><a *ngIf="row.email" [href]="'mailto:' + row.email">{{ row.email }}</a><span *ngIf="!row.email">-</span></td>
                                            <td>{{ row.phone || '-' }}</td>
                                            <td>{{ row.mobile || '-' }}</td>
                                            <td class="text-right">
                                                <button *ngIf="row.clientContact" pButton icon="pi pi-pencil" class="p-button-rounded p-button-text p-button-info" (click)="editDirectoryContact(row)"></button>
                                                <button *ngIf="row.clientContact" pButton icon="pi pi-trash" class="p-button-rounded p-button-text p-button-danger" (click)="deleteDirectoryContact(row)"></button>
                                            </td>
                                        </tr>
                                    </ng-template>
                                    <ng-template pTemplate="emptymessage"><tr><td colspan="6"><div class="empty-state compact"><i class="pi pi-address-book"></i><strong>No contacts available</strong><span>Add a client contact, link a company contact, or assign a client account contact.</span></div></td></tr></ng-template>
                                </p-table>
                            </section>
                        </p-tabpanel>

                        <p-tabpanel [value]="3">
                            <section class="workspace-section">
                                <div class="workspace-header">
                                    <div><h2>Client Accounts</h2><p>{{ clientAccounts().length }} client account{{ clientAccounts().length === 1 ? '' : 's' }} for this CRM client</p></div>
                                </div>
                                <div class="client-account-directory">
                                    @for (account of clientAccounts(); track account.id) {
                                        <article class="client-account-card">
                                            <div class="client-account-card-header">
                                                <div class="client-account-logo" [class.placeholder]="!account.logoUrl">
                                                    @if (account.logoUrl) {
                                                        <img [src]="account.logoUrl" [alt]="account.companyName + ' logo'" />
                                                    } @else {
                                                        <i class="pi pi-briefcase"></i>
                                                    }
                                                </div>
                                                <div>
                                                    <h3>{{ account.companyName }}</h3>
                                                    <div class="directory-badge-list">
                                                        <p-tag [value]="account.category || 'Account'" severity="info"></p-tag>
                                                        <p-tag [value]="account.source || 'Source pending'" severity="secondary"></p-tag>
                                                    </div>
                                                </div>
                                            </div>
                                            <dl class="account-detail-list">
                                                <div><dt>KAM</dt><dd>{{ clientAccountKamName(account) }}</dd></div>
                                                <div><dt>Created</dt><dd>{{ account.createdAt ? (account.createdAt | date:'mediumDate') : '-' }}</dd></div>
                                                <div><dt>Assigned Contacts</dt><dd>{{ accountContacts(account).length }}</dd></div>
                                            </dl>
                                            <div class="account-contact-list">
                                                <span class="account-contact-label">Client Account Contacts</span>
                                                @for (contact of accountContacts(account); track contact.customerId || contact.crmClientContactId || $index) {
                                                    <article class="account-contact-row">
                                                        <div><strong>{{ contact.name || '-' }}</strong><span>{{ contact.email || contact.phone || contact.mobile || 'Contact details pending' }}</span></div>
                                                        <div class="directory-badge-list">
                                                            <p-tag *ngIf="contact.linkedCompanyContact" value="Linked company" severity="success"></p-tag>
                                                            <p-tag [value]="'Account: ' + account.companyName" severity="info"></p-tag>
                                                        </div>
                                                    </article>
                                                } @empty {
                                                    <div class="account-contact-empty">No client account contacts assigned.</div>
                                                }
                                            </div>
                                        </article>
                                    } @empty {
                                        <div class="empty-state"><i class="pi pi-briefcase"></i><strong>No client accounts</strong><span>Create a CRM client account to assign account contacts.</span></div>
                                    }
                                </div>
                            </section>
                        </p-tabpanel>

                        <p-tabpanel [value]="4">
                            <section class="workspace-section">
                                <div class="workspace-header">
                                    <div>
                                        <h2>Customer Documents</h2>
                                        <p>{{ completedComplianceDocumentCount() }} of {{ requiredComplianceDocumentCount() }} required documents complete</p>
                                    </div>
                                    <div class="workspace-actions">
                                        <p-button label="Add Attachment" icon="pi pi-paperclip" severity="secondary" [outlined]="true" (onClick)="showRegistrationAttachmentDialog.set(true)"></p-button>
                                        <p-button label="Add Document" icon="pi pi-upload" (onClick)="showDocumentDialog.set(true)"></p-button>
                                    </div>
                                </div>

                                <div class="compliance-status-card" [class.complete]="missingComplianceDocumentRows().length === 0">
                                    <div class="compliance-status-icon">
                                        <i [class]="missingComplianceDocumentRows().length === 0 ? 'pi pi-check-circle' : 'pi pi-exclamation-triangle'"></i>
                                    </div>
                                    <div>
                                        <h3>{{ missingComplianceDocumentRows().length === 0 ? 'Client compliance is complete' : missingComplianceDocumentRows().length + ' compliance document' + (missingComplianceDocumentRows().length === 1 ? ' is' : 's are') + ' missing' }}</h3>
                                        <p>
                                            @if (missingComplianceDocumentRows().length === 0) {
                                                All required client compliance documents are uploaded.
                                            } @else {
                                                Missing:
                                                @for (missing of missingComplianceDocumentRows(); track missing.key) {
                                                    <strong>{{ missing.label }}</strong>{{ !$last ? ', ' : '' }}
                                                }
                                            }
                                        </p>
                                    </div>
                                </div>

                                <div class="compliance-document-grid">
                                    @for (row of complianceDocumentRows(); track row.key) {
                                        <article class="compliance-document-card" [class.missing]="row.uploads.length === 0" [class.optional]="!row.required">
                                            <div class="document-card-leading">
                                                <span class="document-status-icon">
                                                    <i [class]="row.uploads.length > 0 ? 'pi pi-check' : 'pi pi-times'"></i>
                                                </span>
                                                <div>
                                                    <div class="document-card-title">
                                                        <strong>{{ row.label }}</strong>
                                                        <p-tag [value]="row.required ? 'Required' : 'Optional'" [severity]="row.required ? 'warn' : 'secondary'"></p-tag>
                                                        @if (row.uploads.length > 1) {
                                                            <p-tag [value]="row.uploads.length + ' files'" severity="info"></p-tag>
                                                        }
                                                    </div>
                                                    @if (row.uploads.length > 0) {
                                                        <div class="document-upload-list">
                                                            @for (upload of row.uploads; track upload.id || upload.fileName) {
                                                                <div class="document-upload-row">
                                                                    <div>
                                                                        <a [href]="upload.fileUrl" target="_blank" class="document-file-link">{{ upload.fileName || row.label }}</a>
                                                                        <span class="document-meta">
                                                                            Uploaded {{ upload.uploadedAt ? (upload.uploadedAt | date:'medium') : 'date pending' }}
                                                                            @if (row.source === 'document' && $any(upload).expiryDate) {
                                                                                | Expires {{ $any(upload).expiryDate | date:'mediumDate' }}
                                                                            }
                                                                        </span>
                                                                    </div>
                                                                    @if (row.source === 'registration') {
                                                                        <button pButton icon="pi pi-trash" class="p-button-rounded p-button-text p-button-danger" (click)="deleteRegistrationAttachment($any(upload))"></button>
                                                                    } @else {
                                                                        <button pButton icon="pi pi-trash" class="p-button-rounded p-button-text p-button-danger" (click)="deleteDocument($any(upload))"></button>
                                                                    }
                                                                </div>
                                                            }
                                                        </div>
                                                    } @else {
                                                        <span class="document-missing-copy">{{ row.required ? 'Required for complete client compliance.' : 'Optional reference document not uploaded.' }}</span>
                                                    }
                                                </div>
                                            </div>

                                            <div class="document-card-actions">
                                                <p-button [label]="row.uploads.length > 0 ? 'Add More' : 'Upload'" icon="pi pi-upload" severity="secondary" [outlined]="true" (onClick)="startUploadForComplianceDocument(row)"></p-button>
                                            </div>
                                        </article>
                                    }
                                </div>
                            </section>
                        </p-tabpanel>

                        <p-tabpanel [value]="5">
                            <section class="workspace-section">
                                <div class="workspace-header"><div><h2>Remarks</h2><p>Customer-level master remarks</p></div></div>
                                <article class="note-card"><p>{{ activeClient.remarks || 'No remarks recorded.' }}</p></article>
                                <div class="notes-list mt-3">
                                    @for (note of notes(); track note.id) {
                                        <article class="note-card"><span>{{ note.createdAt | date:'medium' }}</span><p>{{ note.text }}</p></article>
                                    }
                                </div>
                            </section>
                        </p-tabpanel>

                        <p-tabpanel [value]="6">
                            <section class="workspace-section">
                                <div class="workspace-header">
                                    <div><h2>Follow-up Reminders</h2><p>{{ reminders().length }} reminders</p></div>
                                    <div class="workspace-actions"><p-button label="Set Reminder" icon="pi pi-calendar-plus" (onClick)="showReminderDialog.set(true)"></p-button></div>
                                </div>
                                <p-table [value]="reminders()" styleClass="p-datatable-sm reminders-table">
                                    <ng-template pTemplate="header"><tr><th>Task</th><th>Due Date</th><th>Status</th></tr></ng-template>
                                    <ng-template pTemplate="body" let-rem><tr><td>{{ rem.reminderText }}</td><td>{{ rem.dueDate | date:'medium' }}</td><td><p-tag [value]="rem.completed ? 'Completed' : 'Pending'" [severity]="rem.completed ? 'success' : 'warn'"></p-tag></td></tr></ng-template>
                                    <ng-template pTemplate="emptymessage"><tr><td colspan="3"><div class="empty-state compact"><i class="pi pi-calendar"></i><strong>No reminders set</strong><span>Create a follow-up task.</span></div></td></tr></ng-template>
                                </p-table>
                            </section>
                        </p-tabpanel>
                    </p-tabpanels>
                </p-tabs>
            </app-page-data-panel>
        } @else {
            <div class="loading-state"><p-progressSpinner styleClass="w-4rem h-4rem" strokeWidth="4" animationDuration=".5s"></p-progressSpinner><p>Loading Client Details...</p></div>
        }
    </app-page-shell>
</div>

<p-dialog [visible]="showNoteDialog()" (visibleChange)="showNoteDialog.set($event)" header="Add Internal Note" [modal]="true" [style]="{width: '420px'}">
    <textarea pInputTextarea [rows]="4" [autoResize]="true" [ngModel]="newNoteText()" (ngModelChange)="newNoteText.set($event)" placeholder="Type the note"></textarea>
    <ng-template pTemplate="footer"><p-button label="Cancel" icon="pi pi-times" [text]="true" (onClick)="showNoteDialog.set(false)"></p-button><p-button label="Save Note" icon="pi pi-check" (onClick)="saveNote()" [disabled]="!newNoteText()"></p-button></ng-template>
</p-dialog>

<p-dialog [visible]="showRegistrationAttachmentDialog()" (visibleChange)="showRegistrationAttachmentDialog.set($event)" header="Add Registration Attachment" [modal]="true" [style]="{width: '560px'}">
    <div class="grid p-fluid">
        <div class="col-12"><p-select [options]="registrationAttachmentTypes" optionLabel="label" optionValue="value" [ngModel]="registrationAttachmentDraft().category" (ngModelChange)="updateRegistrationAttachmentCategory($event)" styleClass="w-full"></p-select></div>
        <div class="col-12">
            <label class="p-button p-component cursor-pointer">
                <i class="pi pi-upload mr-2"></i>
                <span>{{ uploadingDocument() ? 'Uploading' : 'Choose File' }}</span>
                <input type="file" class="hidden" (change)="uploadRegistrationAttachment($event)" [disabled]="uploadingDocument()">
            </label>
        </div>
    </div>
    <ng-template pTemplate="footer"><p-button label="Close" icon="pi pi-times" [text]="true" (onClick)="showRegistrationAttachmentDialog.set(false)"></p-button></ng-template>
</p-dialog>

<p-dialog
    [visible]="showCommDialog()"
    (visibleChange)="showCommDialog.set($event)"
    [modal]="true"
    [draggable]="false"
    [resizable]="false"
    styleClass="crm-activity-dialog"
    [style]="{ width: '640px', maxWidth: '94vw' }"
    [breakpoints]="{ '960px': '88vw', '640px': '94vw' }">
    <ng-template pTemplate="header">
        <div class="activity-dialog-header">
            <span class="activity-dialog-icon"><i class="pi pi-comments"></i></span>
            <div>
                <h3>Log Communication</h3>
                <p>Capture the latest call, email, or meeting update for this client.</p>
            </div>
        </div>
    </ng-template>

    <div class="activity-dialog-body">
        <section class="activity-dialog-panel">
            <div class="activity-section-title"><i class="pi pi-send"></i><span>Communication Channel</span></div>
            <div class="activity-type-grid">
                <button type="button" class="activity-type-card" [class.active]="newCommLog().type === 'CALL'" (click)="updateCommType('CALL')">
                    <i class="pi pi-phone"></i><span>Call</span>
                </button>
                <button type="button" class="activity-type-card" [class.active]="newCommLog().type === 'EMAIL'" (click)="updateCommType('EMAIL')">
                    <i class="pi pi-envelope"></i><span>Email</span>
                </button>
                <button type="button" class="activity-type-card" [class.active]="newCommLog().type === 'MEETING'" (click)="updateCommType('MEETING')">
                    <i class="pi pi-users"></i><span>Meeting</span>
                </button>
            </div>
        </section>

        <section class="activity-dialog-panel">
            <div class="activity-form-grid">
                <div class="activity-field span-2">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-align-left"></i></p-inputGroupAddon>
                        <p-iftaLabel>
                            <textarea id="clientCommSummary" pInputTextarea [rows]="4" [ngModel]="newCommLog().summary" (ngModelChange)="updateCommLogSummary($event)"></textarea>
                            <label for="clientCommSummary">Activity Summary</label>
                        </p-iftaLabel>
                    </p-inputGroup>
                </div>
                <div class="activity-field span-2">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-calendar-clock"></i></p-inputGroupAddon>
                        <p-iftaLabel>
                            <p-datePicker inputId="clientCommDate" [showTime]="true" [ngModel]="newCommLog().communicationDate" (ngModelChange)="updateCommLogDate($event)" appendTo="body" styleClass="w-full"></p-datePicker>
                            <label for="clientCommDate">Date & Time</label>
                        </p-iftaLabel>
                    </p-inputGroup>
                </div>
            </div>
        </section>
    </div>

    <ng-template pTemplate="footer">
        <div class="activity-dialog-footer">
            <p-button label="Cancel" icon="pi pi-times" severity="secondary" [outlined]="true" (onClick)="showCommDialog.set(false)"></p-button>
            <p-button label="Log Activity" icon="pi pi-check" (onClick)="saveCommLog()" [disabled]="!newCommLog().summary"></p-button>
        </div>
    </ng-template>
</p-dialog>

<p-dialog [visible]="showReminderDialog()" (visibleChange)="showReminderDialog.set($event)" header="Set Follow-up Reminder" [modal]="true" [style]="{width: '460px'}">
    <div class="grid p-fluid">
        <div class="col-12"><input pInputText [ngModel]="newReminder().reminderText" (ngModelChange)="updateReminderText($event)" placeholder="Follow-up task"></div>
        <div class="col-12"><p-datePicker [showTime]="true" [ngModel]="newReminder().dueDate" (ngModelChange)="updateReminderDueDate($event)"></p-datePicker></div>
    </div>
    <ng-template pTemplate="footer"><p-button label="Cancel" icon="pi pi-times" [text]="true" (onClick)="showReminderDialog.set(false)"></p-button><p-button label="Set Reminder" icon="pi pi-calendar-plus" (onClick)="saveReminder()" [disabled]="!newReminder().reminderText"></p-button></ng-template>
</p-dialog>

<app-party-quick-create-dialog
    [visible]="showCustomerQuickCreateDialog()"
    (visibleChange)="showCustomerQuickCreateDialog.set($event)"
    mode="customer"
    [record]="quickCreateContactDraft()"
    [countries]="quickCreateCountries"
    [owningCompany]="quickCreateOwningCompany"
    [contactCompany]="quickCreateContactCompany()"
    [titleOverride]="'Add Customer Contact'"
    submitLabel="Save Contact"
    (created)="onCustomerQuickCreateCompleted($event)"
    (failed)="onCustomerQuickCreateFailed($event)">
</app-party-quick-create-dialog>

<p-dialog [visible]="showMasterCommDialog()" (visibleChange)="showMasterCommDialog.set($event)" [header]="masterCommunication().id ? 'Edit Contact' : 'Add Contact'" [modal]="true" [style]="{width: '620px'}">
    <div class="grid p-fluid">
        <div class="col-12 md:col-6"><input pInputText [ngModel]="masterCommunication().communicationType" (ngModelChange)="updateMasterCommunicationField('communicationType', $event)" placeholder="Type"></div>
        <div class="col-12 md:col-6"><input pInputText [ngModel]="masterCommunication().name" (ngModelChange)="updateMasterCommunicationField('name', $event)" placeholder="Name"></div>
        <div class="col-12 md:col-6"><input pInputText [ngModel]="masterCommunication().email" (ngModelChange)="updateMasterCommunicationField('email', $event)" placeholder="Email"></div>
        <div class="col-12 md:col-6"><input pInputText [ngModel]="masterCommunication().phone" (ngModelChange)="updateMasterCommunicationField('phone', $event)" placeholder="Phone"></div>
        <div class="col-12 md:col-6"><input pInputText [ngModel]="masterCommunication().mobile" (ngModelChange)="updateMasterCommunicationField('mobile', $event)" placeholder="Mobile"></div>
        <div class="col-12 md:col-6"><input pInputText [ngModel]="masterCommunication().fax" (ngModelChange)="updateMasterCommunicationField('fax', $event)" placeholder="Fax"></div>
        <div class="col-12 md:col-6"><input pInputText [ngModel]="masterCommunication().extension" (ngModelChange)="updateMasterCommunicationField('extension', $event)" placeholder="Extension"></div>
        <div class="col-12 md:col-6"><input pInputText [ngModel]="masterCommunication().website" (ngModelChange)="updateMasterCommunicationField('website', $event)" placeholder="Website"></div>
        <label class="col-12 primary-contact-toggle">
            <input type="checkbox" [ngModel]="masterCommunication().primaryRecord" (ngModelChange)="updateMasterCommunicationField('primaryRecord', $event)">
            <span>Use as primary client contact</span>
        </label>
    </div>
    <ng-template pTemplate="footer"><p-button label="Cancel" icon="pi pi-times" [text]="true" (onClick)="showMasterCommDialog.set(false)"></p-button><p-button label="Save Contact" icon="pi pi-check" (onClick)="saveMasterCommunication()"></p-button></ng-template>
</p-dialog>

<p-dialog
    [visible]="showDocumentDialog()"
    (visibleChange)="showDocumentDialog.set($event)"
    [modal]="true"
    [draggable]="false"
    [resizable]="false"
    styleClass="crm-document-dialog"
    [style]="{ width: '720px', maxWidth: '94vw' }"
    [breakpoints]="{ '960px': '88vw', '640px': '94vw' }">
    <ng-template pTemplate="header">
        <div class="document-dialog-header">
            <span class="dialog-icon"><i class="pi pi-file-plus"></i></span>
            <div>
                <h3>Add Document</h3>
                <p>Upload compliance, contract, and customer reference files.</p>
            </div>
        </div>
    </ng-template>

    <div class="document-dialog-body">
        <section class="document-dialog-panel metadata-panel">
            <div class="dialog-section-title">
                <i class="pi pi-sliders-h"></i>
                <span>Document Details</span>
            </div>

            <div class="document-form-grid">
                <div class="document-field span-2">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-pencil"></i></p-inputGroupAddon>
                        <p-iftaLabel>
                            <input
                                id="clientDocumentTitle"
                                pInputText
                                [ngModel]="documentDraft().title"
                                (ngModelChange)="updateDocumentDraftField('title', $event)">
                            <label for="clientDocumentTitle">Document Title</label>
                        </p-iftaLabel>
                    </p-inputGroup>
                </div>

                <div class="document-field">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-folder"></i></p-inputGroupAddon>
                        <p-iftaLabel>
                            <p-select
                                inputId="clientDocumentCategory"
                                [options]="documentCategories"
                                optionLabel="label"
                                optionValue="value"
                                [ngModel]="documentDraft().category"
                                (ngModelChange)="updateDocumentDraftField('category', $event)"
                                appendTo="body"
                                styleClass="w-full">
                            </p-select>
                            <label for="clientDocumentCategory">Category</label>
                        </p-iftaLabel>
                    </p-inputGroup>
                </div>

                <div class="document-field">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-calendar"></i></p-inputGroupAddon>
                        <p-iftaLabel>
                            <p-datePicker
                                inputId="clientDocumentExpiry"
                                [ngModel]="documentDraft().expiryDate"
                                (ngModelChange)="updateDocumentDraftField('expiryDate', $event)"
                                appendTo="body"
                                styleClass="w-full">
                            </p-datePicker>
                            <label for="clientDocumentExpiry">Expiry Date</label>
                        </p-iftaLabel>
                    </p-inputGroup>
                </div>
            </div>
        </section>

        <section class="document-upload-panel">
            <div class="upload-illustration">
                <i class="pi pi-cloud-upload"></i>
            </div>
            <div class="upload-copy">
                <h4>{{ uploadingDocument() ? 'Uploading document' : 'Choose a document' }}</h4>
                <p>Attach PDF, image, or office files for this CRM client.</p>
            </div>
            <label class="document-upload-button" [class.loading]="uploadingDocument()">
                <i class="pi" [class.pi-spin]="uploadingDocument()" [class.pi-spinner]="uploadingDocument()" [class.pi-upload]="!uploadingDocument()"></i>
                <span>{{ uploadingDocument() ? 'Uploading...' : 'Choose Document' }}</span>
                <input type="file" class="hidden" (change)="uploadDocument($event)" [disabled]="uploadingDocument()">
            </label>
        </section>
    </div>

    <ng-template pTemplate="footer">
        <div class="document-dialog-footer">
            <p-button label="Close" icon="pi pi-times" severity="secondary" [outlined]="true" (onClick)="showDocumentDialog.set(false)"></p-button>
        </div>
    </ng-template>
</p-dialog>
